import { FC, ReactNode, useEffect, useRef } from 'react';

import { MedicalImageRender } from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import { useAppSelector } from '@/shared/hooks';

import { assetsModel } from '@/entities/assets';

import styles from './DocumentImageDisplay.module.scss';
import { isDicomSliceAsset } from './lib';

/**
 * The medical renderer only starts when a report page is opened. The picker
 * and deep-linked documents boot the slice-editor view here when needed.
 */
const useEnsureSliceEditorView = (enabled: boolean) => {
  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (MedicalImageRender.isViewExist('sliceEditor')) {
      return;
    }
    MedicalImageRender.launch();
    if (!MedicalImageRender.isViewExist('sliceEditor')) {
      // The document is printed from the DOM (window.print), so the WebGL
      // canvas must keep its drawing buffer between frames or it prints blank.
      MedicalImageRender.addSliceEditor(true);
    }
  }, [enabled]);
};

export const DocumentImageDisplay: FC<{
  assetID: string;
  /** Slice-editor view key the canvas binds under — not a patient-document
      id; callers use the document id, or a distinct prefix to isolate a
      view (e.g. 'picker-<reportID>' for the asset-picker lightbox). */
  documentID: string;
  renderFallback: () => ReactNode;
}> = ({ assetID, documentID, renderFallback }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const asset = useAppSelector(assetsModel.selectors.selectAssetByID(assetID));
  const rendersDicom = Boolean(asset && isDicomSliceAsset(asset));

  useEnsureSliceEditorView(rendersDicom);

  useEffect(() => {
    if (!rendersDicom || !containerRef.current) {
      return;
    }
    // Same binding the tooth page uses: a canvas keyed by the asset ID inside
    // the shared slice-editor view. The renderer loads the DICOM itself.
    MedicalImageRender.insertSlicesToExistingImages({
      toothID: documentID,
      container: containerRef.current,
    });
  }, [rendersDicom, documentID]);

  if (!rendersDicom) {
    return <>{renderFallback()}</>;
  }

  return (
    <div className={styles.host} data-component="document-image-display">
      <div ref={containerRef} className={styles.slice}>
        <canvas id={assetID} className={styles.sliceCanvas} />
      </div>
    </div>
  );
};
